import { useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { addSupportMessage, createSupportTicket, loadSupportMessages, loadSupportTickets } from '../../services/support';
import logger from "../../utils/logger";
import { fmtDateTime } from '../../utils/ruDates';
const MODULE_OPTIONS = [{
  value: '',
  label: 'Без привязки'
}, {
  value: 'marketplace',
  label: 'Маркетплейсы'
}, {
  value: 'projects',
  label: 'Проекты'
}, {
  value: 'tasks',
  label: 'Задачи'
}, {
  value: 'chat',
  label: 'Чат'
}, {
  value: 'crm',
  label: 'CRM'
}, {
  value: 'settings',
  label: 'Настройки'
}, {
  value: 'account',
  label: 'Личный кабинет'
}, {
  value: 'ozon_analytics',
  label: 'OZON Аналитика'
}, {
  value: 'ozon_seo',
  label: 'OZON SEO-уникализация'
}, {
  value: 'ozon_seo_ai',
  label: 'OZON SEO AI'
}, {
  value: 'ozon_storage',
  label: 'Платное хранение'
}, {
  value: 'tax_reports',
  label: 'Налоговые отчеты'
}];
export const SUPPORT_OPEN_TICKET_KEY = 'anylex_support_open_ticket';
export const SUPPORT_OPEN_TICKET_EVENT = 'anylex:open-support-ticket';
const STATUS_LABELS = {
  open: 'Открыт',
  pending: 'В работе',
  in_progress: 'В работе',
  waiting_user: 'Ждёт ответа',
  closed: 'Закрыт'
};
const PRIORITY_LABELS = {
  low: 'Низкий',
  normal: 'Обычный',
  high: 'Высокий',
  urgent: 'Критический'
};
const TYPE_LABELS = {
  bug: 'Баг-репорт',
  support: 'Обращение',
  referral_withdrawal: 'Вывод реферальных средств'
};
const MODULE_LABELS = MODULE_OPTIONS.reduce((acc, option) => {
  if (option.value) acc[option.value] = option.label;
  return acc;
}, {});

// Сырое значение из API лучше показать как есть, чем спрятать: новый статус
// в админке не должен превращаться в пустое место в кабинете.
const labelFor = (dictionary, value) => {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return '';
  return dictionary[key] || key;
};
function SupportPanel() {
  const {
    request,
    showStatus
  } = useApi();
  const {
    user
  } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [ticketsBusy, setTicketsBusy] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState('');
  const [messages, setMessages] = useState([]);
  const [messagesBusy, setMessagesBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  // Тикет, который просили открыть извне: ссылка из письма/уведомления.
  // Применяется после загрузки списка, иначе автовыбор первого тикета его затрёт.
  const [pendingTicketId, setPendingTicketId] = useState(() => {
    try {
      const stored = sessionStorage.getItem(SUPPORT_OPEN_TICKET_KEY);
      if (stored) sessionStorage.removeItem(SUPPORT_OPEN_TICKET_KEY);
      return stored || '';
    } catch (e) {
      return '';
    }
  });
  const [form, setForm] = useState({
    ticket_type: 'bug',
    subject: '',
    priority: 'normal',
    module_key: '',
    marketplace_account_id: '',
    message: ''
  });
  const [cabinets, setCabinets] = useState([]);
  useEffect(() => {
    request('/api/v1/marketplaces/', {}).then(data => setCabinets(Array.isArray(data) ? data : [])).catch(error => logger.error(error));
    // eslint-disable-next-line
  }, []);
  const cabinetNames = useMemo(() => cabinets.reduce((acc, c) => {
    acc[String(c.id)] = c.account_name;
    return acc;
  }, {}), [cabinets]);
  const visibleModuleOptions = useMemo(() => {
    const moduleFlags = user?.module_flags || {};
    return MODULE_OPTIONS.filter(option => {
      if (!option.value) return true;
      return !['hidden', 'disabled'].includes(moduleFlags[option.value] || 'enabled');
    });
  }, [user]);
  const selectedTicket = useMemo(() => tickets.find(ticket => String(ticket.id) === String(selectedTicketId)) || null, [tickets, selectedTicketId]);
  const refreshTickets = async () => {
    await loadSupportTickets(request, setTickets, setTicketsBusy);
  };
  useEffect(() => {
    refreshTickets().catch(error => {
      logger.error(error);
      showStatus('error', 'Не удалось загрузить обращения.');
    });
    try {
      const raw = sessionStorage.getItem('anylex_support_prefill');
      if (raw) {
        const prefill = JSON.parse(raw);
        setForm(prev => ({
          ...prev,
          ticket_type: prefill.ticket_type || prev.ticket_type,
          subject: prefill.subject || prev.subject,
          priority: prefill.priority || prev.priority,
          module_key: prefill.module_key !== undefined ? prefill.module_key : prev.module_key
        }));
        sessionStorage.removeItem('anylex_support_prefill');
      }
    } catch (e) {
      logger.error(e);
    }
  }, []);
  useEffect(() => {
    const handleOpenTicket = event => {
      const ticketId = event?.detail?.ticketId;
      if (ticketId) setPendingTicketId(String(ticketId));
    };
    window.addEventListener(SUPPORT_OPEN_TICKET_EVENT, handleOpenTicket);
    return () => window.removeEventListener(SUPPORT_OPEN_TICKET_EVENT, handleOpenTicket);
  }, []);
  useEffect(() => {
    if (!tickets.length) {
      setSelectedTicketId('');
      return;
    }
    if (pendingTicketId) {
      if (tickets.some(ticket => String(ticket.id) === String(pendingTicketId))) {
        setSelectedTicketId(String(pendingTicketId));
      }
      setPendingTicketId('');
      return;
    }
    if (!selectedTicketId || !tickets.some(ticket => String(ticket.id) === String(selectedTicketId))) {
      setSelectedTicketId(String(tickets[0].id));
    }
  }, [tickets, selectedTicketId, pendingTicketId]);
  useEffect(() => {
    if (!selectedTicketId) {
      setMessages([]);
      return;
    }
    loadSupportMessages(request, selectedTicketId, setMessages, setMessagesBusy).catch(error => {
      logger.error(error);
      showStatus('error', 'Не удалось загрузить переписку.');
    });
  }, [request, selectedTicketId, showStatus]);
  const handleCreateTicket = async event => {
    event.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      showStatus('error', 'Заполните тему и описание.');
      return;
    }
    setSubmitBusy(true);
    try {
      const created = await createSupportTicket(request, {
        ...form,
        marketplace_account_id: form.marketplace_account_id || null,
        page_url: `${window.location.pathname}${window.location.search}`
      });
      showStatus('success', form.ticket_type === 'bug' ? 'Баг-репорт отправлен.' : 'Обращение отправлено.');
      setForm({
        ticket_type: 'bug',
        subject: '',
        priority: 'normal',
        module_key: '',
        marketplace_account_id: form.marketplace_account_id,
        message: ''
      });
      await refreshTickets();
      if (created?.id) {
        setSelectedTicketId(String(created.id));
      }
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось отправить обращение.');
    } finally {
      setSubmitBusy(false);
    }
  };
  const handleReply = async event => {
    event.preventDefault();
    if (!selectedTicketId || !replyBody.trim()) {
      return;
    }
    setReplyBusy(true);
    try {
      await addSupportMessage(request, selectedTicketId, {
        body: replyBody.trim()
      });
      setReplyBody('');
      await loadSupportMessages(request, selectedTicketId, setMessages, setMessagesBusy);
      await refreshTickets();
      showStatus('success', 'Сообщение отправлено.');
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось отправить сообщение.');
    } finally {
      setReplyBusy(false);
    }
  };
  return <div className="panel">
            <h3>Помощь и техподдержка</h3>
            <p>
                Здесь можно отправить обычное обращение или баг-репорт. Для багов фиксируется раздел и текущая страница.
            </p>

            <div className="support-layout">
                <div className="panel support-new-ticket">
                    <h4>Новое обращение</h4>
                    <form onSubmit={handleCreateTicket} className="support-form">
                        <div className="support-form__row">
                            <label className="support-form__field">
                                <span>Тип</span>
                                <select value={form.ticket_type} onChange={event => setForm(prev => ({
                ...prev,
                ticket_type: event.target.value
              }))}>
                  
                                    <option value="bug">Баг-репорт</option>
                                    <option value="support">Обращение</option>
                                </select>
                            </label>
                            <label className="support-form__field">
                                <span>Приоритет</span>
                                <select value={form.priority} onChange={event => setForm(prev => ({
                ...prev,
                priority: event.target.value
              }))}>
                  
                                    <option value="low">Низкий</option>
                                    <option value="normal">Обычный</option>
                                    <option value="high">Высокий</option>
                                </select>
                            </label>
                            <label className="support-form__field">
                                <span>Раздел</span>
                                <select value={form.module_key} onChange={event => setForm(prev => ({
                ...prev,
                module_key: event.target.value
              }))}>
                  
                                    {visibleModuleOptions.map(option => <option key={option.value || 'none'} value={option.value}>
                                            {option.label}
                                        </option>)}
                                </select>
                            </label>
                            <label className="support-form__field">
                                <span>Кабинет</span>
                                <select value={form.marketplace_account_id} onChange={event => setForm(prev => ({
                ...prev,
                marketplace_account_id: event.target.value
              }))}>
                                    <option value="">Не относится к кабинету</option>
                                    {cabinets.map(c => <option key={c.id} value={c.id}>
                                            {c.account_name} ({c.marketplace_type === 'ozon' ? 'Ozon' : 'WB'})
                                        </option>)}
                                </select>
                            </label>
                        </div>

                        <label className="support-form__field">
                            <span>Тема</span>
                            <input type="text" value={form.subject} onChange={event => setForm(prev => ({
              ...prev,
              subject: event.target.value
            }))} placeholder="Коротко: что произошло" />
              
                        </label>

                        <label className="support-form__field">
                            <span>Описание</span>
                            <textarea rows={6} value={form.message} onChange={event => setForm(prev => ({
              ...prev,
              message: event.target.value
            }))} placeholder="Шаги, ожидаемое поведение, фактический результат" />
              
                        </label>

                        <div>
                            <button type="submit" disabled={submitBusy}>
                                {submitBusy ? 'Отправка...' : 'Отправить'}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="panel" style={{
        margin: 0
      }}>
                    <h4 style={{
          marginTop: 0
        }}>Мои обращения</h4>
                    {ticketsBusy ? <p>Загрузка...</p> : tickets.length === 0 ? <p>Пока нет обращений.</p> : <div className="support-ticket-list">
                            {tickets.map(ticket => <button key={ticket.id} type="button" className={`support-ticket-item${String(ticket.id) === String(selectedTicketId) ? ' support-ticket-item--active' : ''}`} onClick={() => setSelectedTicketId(String(ticket.id))}>
              
                                    <div className="support-ticket-item__title">
                                        {ticket.ticket_type === 'bug' ? 'Баг' : 'Обращение'}: {ticket.subject}
                                    </div>
                                    <div className="support-ticket-item__meta">
                                        {labelFor(STATUS_LABELS, ticket.status)} • приоритет: {labelFor(PRIORITY_LABELS, ticket.priority)}
                                        {ticket.module_key ? ` • ${labelFor(MODULE_LABELS, ticket.module_key)}` : ''}
                                        {ticket.marketplace_account_id && cabinetNames[String(ticket.marketplace_account_id)] ? ` • кабинет: ${cabinetNames[String(ticket.marketplace_account_id)]}` : ''}
                                    </div>
                                </button>)}
                        </div>}
                </div>
            </div>

            {selectedTicket && <div className="panel support-thread">
                    <h4>{selectedTicket.subject}</h4>
                    <p className="muted support-thread__meta">
                        {labelFor(TYPE_LABELS, selectedTicket.ticket_type)} • {labelFor(STATUS_LABELS, selectedTicket.status)} • приоритет: {labelFor(PRIORITY_LABELS, selectedTicket.priority)}
                        {selectedTicket.module_key ? ` • раздел: ${labelFor(MODULE_LABELS, selectedTicket.module_key)}` : ''}
                        {selectedTicket.marketplace_account_id && cabinetNames[String(selectedTicket.marketplace_account_id)] ? ` • кабинет: ${cabinetNames[String(selectedTicket.marketplace_account_id)]}` : ''}
                        {selectedTicket.page_url ? ` • страница: ${selectedTicket.page_url}` : ''}
                    </p>

                    <div className="support-messages">
                        {messagesBusy ? <p>Загрузка переписки...</p> : messages.length === 0 ? <p>Сообщений пока нет.</p> : messages.map(message => <div key={message.id} className={`support-message${message.is_staff ? ' support-message--staff' : ''}`}>
                                    <div className="support-message__date">
                                        <strong className="support-message__author">
                                            {message.author_label || (message.is_staff ? 'Поддержка' : 'Вы')}
                                        </strong>
                                        {' • '}
                                        {fmtDateTime(message.created_at)}
                                    </div>
                                    <div>{message.body}</div>
                                </div>)}
                    </div>

                    <form onSubmit={handleReply} className="support-reply-form">
                        <textarea rows={4} value={replyBody} onChange={event => setReplyBody(event.target.value)} placeholder="Дополнить обращение" />
          
                        <div>
                            <button type="submit" className="ghost" disabled={replyBusy || !selectedTicketId}>
                                {replyBusy ? 'Отправка...' : 'Отправить сообщение'}
                            </button>
                        </div>
                    </form>
                </div>}
        </div>;
}
export default SupportPanel;
